@if (isLoading) {
<div class="loader-overlay" role="status" aria-live="polite">
  <div class="heart-wrapper" aria-hidden="true">
    <svg class="heart-svg" viewBox="0 0 24 24">
      <path class="heart-shape" d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 6 4 4 6.5 4
                 8.24 4 9.91 5 10.54 6.36 11.17 5 12.76 4 14.5 4 17 4 19 6 19 8.5
                 c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
      <polyline class="ekg" points="3 12 7 12 9 8 11 15 13 6 15 12 21 12" fill="none" />
    </svg>
  </div>
  <p class="loader-text mt-3 fw-semibold text-dark">Loading patients...</p>
</div>
}

@if (!!patients && !isLoading) {
<app-portal-navigation [portalName]="portalName" [portalIcon]="portalIcon" [portalClass]="portalClass"
  collapseId="patientsMenu" [firstName]="userProfile?.firstName ?? ''" [lastName]="userProfile?.lastName ?? ''"
  [primaryItems]="primaryNavigationItems" [profileRoute]="profileRoute" [additionalItems]="additionalNavigationItems">
</app-portal-navigation>

<div class="patients-view container-fluid p-3 p-md-4">
  <!-- Hero Banner -->
  <div class="booking-hero card border-0 mb-4">
    <div class="booking-hero__content">
      <div>
        <div class="booking-hero__eyebrow">Practice Directory</div>
        <h2 class="booking-hero__title mb-2">Patients & Medical Records</h2>
        <div class="booking-hero__meta">
          <span><i class="bi bi-people-fill me-1 text-primary"></i>Total registered:
            {{ patients.length }}</span>
          <span><i class="bi bi-shield-check me-1 text-success"></i>Records secure & encrypted</span>
        </div>
      </div>

      <div class="booking-hero__status">
        <button class="btn btn-primary" (click)="goToCreatePatient()">
          <i class="bi bi-person-plus-fill me-1"></i>
          Create New Patient
        </button>
      </div>
    </div>
  </div>

  <!-- Alert Note -->
  <div class="alert alert-info d-flex align-items-start patients-note mb-4">
    <i class="bi bi-info-circle-fill me-2 mt-1 fs-5"></i>
    <div>
      Access patient demographics, create consultation records, schedule appointments, and review
      past medical history directly from each card's action menu.
    </div>
  </div>

  <!-- Patient Directory Header -->
  <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
    <div>
      <h5 class="sub-title mb-0">Patient Directory</h5>
      <small class="text-muted">Manage care records and schedule visits.</small>
    </div>
    <span class="badge bg-primary-subtle text-primary border border-primary-subtle rounded-pill px-3 py-2">
      {{ patients.length }} patient{{ patients.length === 1 ? '' : 's' }}
    </span>
  </div>

  @if (patients.length === 0) {
  <div class="empty-state">
    <i class="bi bi-person-x fs-1 d-block mb-2 text-primary"></i>
    <h5>No patients found</h5>
    <p class="text-muted small">Register your first patient using the button above.</p>
  </div>
  }

  <!-- Patients Cards Grid -->
  <div class="row g-3">
    @for (p of paginatedPatients; track p.id) {
    <div class="col-12 col-xl-6">
      <div class="card booking-card patient-item-card h-100 position-relative">
        <div class="card-header booking-card__header">
          <div>
            <div class="booking-card__eyebrow">Patient File</div>
            <h4 class="patient-name sub-title mb-0">
              {{ p.firstName }} {{ p.lastName }}
              <span class="patient-id-copy ms-1" (click)="copyPatientId(p.id)" title="{{ p.id }}"
                style="cursor: pointer; vertical-align: middle">
                @if (copiedPatientId === p.id) {
                <i class="bi bi-check-circle-fill text-success" style="font-size: 0.75rem"></i>
                } @else {
                <i class="bi bi-clipboard text-muted" style="font-size: 0.75rem"></i>
                }
              </span>
            </h4>
          </div>

          <!-- Actions Dropdown -->
          <div class="dropdown">
            <button class="btn btn-sm btn-light border dropdown-toggle" type="button" data-bs-toggle="dropdown"
              aria-expanded="false" aria-label="Patient actions">
              <i class="bi bi-three-dots-vertical me-1"></i>Actions
            </button>
            <ul class="dropdown-menu dropdown-menu-end shadow-sm">
              <li>
                <a class="dropdown-item" [class.disabled]="!hasRequiredProfileCompletion(p)"
                  [attr.aria-disabled]="!hasRequiredProfileCompletion(p)"
                  [attr.title]="profileCompletionAccessMessage(p)" (click)="
                        hasRequiredProfileCompletion(p) && p.id ? downloadPatientPdf(p.id) : null
                      ">
                  <i class="bi bi-download me-2 text-primary"></i>Download PDF
                </a>
              </li>
              <li>
                <a class="dropdown-item" [class.disabled]="!hasRequiredProfileCompletion(p)"
                  [attr.aria-disabled]="!hasRequiredProfileCompletion(p)"
                  [attr.title]="profileCompletionAccessMessage(p)"
                  (click)="hasRequiredProfileCompletion(p) && goToScheduleAppointment(p)">
                  <i class="bi bi-calendar-plus me-2 text-primary"></i>Schedule Appointment
                </a>
              </li>
              @if ('DOCTOR' | hasRole) {
              <li>
                <a class="dropdown-item" (click)="goToPatientMedicalHistory(p)">
                  <i class="bi bi-clock-history me-2 text-primary"></i>View Medical History
                </a>
              </li>
              }
              <li>
                <a class="dropdown-item" [class.disabled]="!hasRequiredProfileCompletion(p)"
                  [attr.aria-disabled]="!hasRequiredProfileCompletion(p)"
                  [attr.title]="profileCompletionAccessMessage(p)"
                  (click)="hasRequiredProfileCompletion(p) && goToPatientDetails(p)">
                  <i class="bi bi-person-lines-fill me-2 text-primary"></i>View Patient Details
                </a>
              </li>
              <li>
                <a class="dropdown-item" (click)="goToEditPatient(p)">
                  <i class="bi bi-pencil me-2 text-primary"></i>Edit Patient
                </a>
              </li>
              <li>
                <hr class="dropdown-divider" />
              </li>
              <li>
                <a class="dropdown-item text-danger" (click)="confirmDelete(p)" data-bs-toggle="modal"
                  data-bs-target="#deletePatientModal">
                  <i class="bi bi-trash me-2"></i>Delete Patient
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div class="card-body p-3">
          <div class="detail-matrix mb-2">
            <div class="detail-chip">
              <span class="detail-chip__label">Gender / DOB</span>
              <span class="detail-chip__value">{{ p.gender || '—' }} &bull; {{ p.dateOfBirth ?? '' | age }} yrs</span>
            </div>

            <div class="detail-chip">
              <span class="detail-chip__label">Blood Type</span>
              <span class="detail-chip__value">{{ p.bloodType || '—' }}</span>
            </div>

            <div class="detail-chip">
              <span class="detail-chip__label">Medical Aid</span>
              <span class="detail-chip__value text-truncate">{{
                p.clinicPatient?.medicalAidScheme || '—'
                }}</span>
            </div>

            <div class="detail-chip">
              <span class="detail-chip__label">Medical Aid Plan</span>
              <span class="detail-chip__value text-truncate">{{
                p.clinicPatient?.medicalAidPlan || '—'
                }}</span>
            </div>
          </div>

          <!-- Address Chip -->
          <div class="detail-chip w-100 mt-2">
            <span class="detail-chip__label">Address</span>
            <span class="detail-chip__value small">
              <i class="bi bi-geo-alt me-1 text-muted"></i>
              {{ p.clinicPatient?.addressLine1 || 'No address' }}
              @if (p.clinicPatient?.city) {
              , {{ p.clinicPatient?.city }}
              }
              @if (p.clinicPatient?.postalCode) {
              , {{ p.clinicPatient?.postalCode }}
              }
            </span>
          </div>

          <!-- Profile Completion -->
          <div class="mt-3">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <span class="detail-chip__label mb-0">Profile Completion</span>
              <span class="badge rounded-pill bg-{{
                      profileCompletionColor(p.profileCompletionPercentage)
                    }}-subtle
                           text-{{ profileCompletionColor(p.profileCompletionPercentage) }}
                           border border-{{
                      profileCompletionColor(p.profileCompletionPercentage)
                    }}-subtle
                           px-2 py-1" style="font-size: 0.7rem">
                {{ profileCompletionLabel(p.profileCompletionPercentage) }}
                · {{ p.profileCompletionPercentage ?? 0 }}%
              </span>
            </div>
            <div class="progress" style="height: 5px; border-radius: 99px"
              [attr.title]="profileCompletionAccessMessage(p)">
              <div class="progress-bar bg-{{
                      profileCompletionColor(p.profileCompletionPercentage)
                    }}" role="progressbar" [style.width.%]="p.profileCompletionPercentage ?? 0"
                [attr.aria-valuenow]="p.profileCompletionPercentage ?? 0" aria-valuemin="0" aria-valuemax="100"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
    }
  </div>

  <!-- Pagination Controls -->
  @if (patientsPageSize > 1) {
  <div class="d-flex align-items-center justify-content-between mt-4 pt-3 border-top">
    <small class="text-muted">Page {{ patientsCurrentPage }} of {{ patientsPageSize }}</small>
    <div class="btn-group btn-group-sm" role="group">
      <button type="button" class="btn btn-outline-secondary" [disabled]="patientsCurrentPage === 1"
        (click)="patientsPreviousPage()">
        <i class="bi bi-chevron-left"></i> Previous
      </button>
      <button type="button" class="btn btn-outline-secondary" [disabled]="patientsCurrentPage === patientsPageSize"
        (click)="patientsNextPage()">
        Next <i class="bi bi-chevron-right"></i>
      </button>
    </div>
  </div>
  }
</div>
}

<!-- Delete Confirm Modal -->
<div class="modal fade" id="deletePatientModal" tabindex="-1" aria-labelledby="deletePatientModalLabel"
  aria-hidden="true">
  <div class="modal-dialog modal-dialog-centered">
    <div class="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
      <div class="modal-header bg-danger-subtle border-0 py-3">
        <h5 class="modal-title fw-bold text-danger" id="deletePatientModalLabel">
          <i class="bi bi-exclamation-triangle-fill me-2"></i>Delete Patient
        </h5>
        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
      </div>
      <div class="modal-body p-4">
        Are you sure you want to deactivate
        <strong>{{ patientToDelete?.firstName }} {{ patientToDelete?.lastName }}</strong>? This action cannot be undone.
      </div>
      <div class="modal-footer border-0 p-3 bg-light">
        <button type="button" id="deleteModalCloseBtn" class="btn btn-outline-secondary" data-bs-dismiss="modal">
          Cancel
        </button>
        <button type="button" class="btn btn-danger px-4" (click)="deletePatient()" [disabled]="isDeleting">
          @if (isDeleting) {
          <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
          }
          Delete
        </button>
      </div>
    </div>
  </div>
</div>